import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, PACKAGE_SIZES } from "@/lib/marketplace";
import ConditionPicker from "@/components/ConditionPicker";
import ClothingPickers from "@/components/ClothingPickers";
import BestOfferPricing from "@/components/BestOfferPricing";
import { compressImage } from "@/lib/imageCompress";
import { ShieldCheck, AlertTriangle, ArrowRight, Camera, Loader2, X, Upload, Truck, Package, HeartHandshake } from "lucide-react";

const MAX_PHOTOS = 5;
const TITLE_LIMIT = 60;
const TITLE_REQUIRED_MESSAGE = "Please enter a title for your item (up to 60 characters)";

export default function CreateListing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kids, setKids] = useState([]);
  // Iteration 286 — verified household causes for the fundraise picker.
  const [causes, setCauses] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", price: "",
    category: "junior_hub", condition: "used_good",
    secondary_categories: [],
    // Iteration 274 — clothing attributes (only sent when
    // category === "clothing"; brand is parent free-text with
    // suggestions, the rest are fixed picks).
    clothing_subcategory: "", clothing_gender: "", clothing_size: "", clothing_brand: "",
    photos: [], kid_id: "",
    // Iteration 128 — Royal Mail is the platform-recommended default so
    // no listing ships without at least one buyer-selectable delivery
    // path. Parents can still toggle it off in the form.
    shipping_available: true, package_size: "small",
    // Iteration 174 — Royal Mail parcel weight override (grams). Kept
    // as a string so an empty input is preserved; submitted only when
    // the parent has typed a real value.
    weight_grams: "",
    weight_kg: "",
    // Iteration 286 — Fundraise for a Cause (optional; only sent when
    // the toggle is on and a verified cause is picked).
    fundraise: false,
    cause_kid_id: "",
    cause_split_pct: 100,
    // "Pay What You Can" mode — parent-side toggle. When true, `price` is
    // treated as a suggested minimum and the buyer picks the actual amount.
    pay_what_you_can: false,
    // Iteration 301b — Best Offer secret floor price (parity with Kid
    // Zone dual controls; validated floor <= list before submit).
    floor_pence: 0,
    // Parents get to choose whether the listing goes live immediately or
    // sits in the Pending Approvals queue. Default: submit for approval
    // (matches the previous behaviour for backwards-compat).
    publish_directly: false,
  });
  const [uploading, setUploading] = useState(false);
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [titleErr, setTitleErr] = useState("");
  // Iteration 301 — the clothing sub-category panel expands when Clothing
  // is the primary OR one of the secondary category picks.
  const clothingPicked = form.category === "clothing" || (form.secondary_categories || []).includes("clothing");
  // Iteration 301b — Best Offer dual controls (parity with Kid Zone):
  // list price in pence + secret floor, with the floor<=list guardrail.
  const pricePence = Math.max(0, Math.round(parseFloat(form.price || "0") * 100) || 0);
  const floorTooHigh = form.pay_what_you_can && pricePence > 0 && form.floor_pence > pricePence;

  useEffect(() => {
    if (user && user !== false) {
      api.get("/kids").then(({ data }) => {
        setKids(data || []);
        if (data?.length && !form.kid_id) setForm((p) => ({ ...p, kid_id: data[0].id }));
      });
      // Iteration 286 — household causes for the fundraise picker.
      api.get("/causes").then(({ data }) => setCauses(data?.causes || [])).catch(() => {});
    }
  }, [user]); // eslint-disable-line

  if (user === null) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><div className="p-20 text-center">Loading…</div></div>;
  if (user === false) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><div className="p-20 text-center text-slate-500">Please <Link to="/auth?mode=login" className="text-blue-600 font-semibold">sign in</Link> first.</div></div>;

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const slotsLeft = MAX_PHOTOS - form.photos.length;
    const batch = files.slice(0, slotsLeft);
    setErr(""); setUploading(true);
    const newUrls = [];
    for (const f of batch) {
      // Client-side compress before upload (max 1200px, JPEG q0.85) so the
      // proxy round-trip never times out on slow mobile networks.
      const compressed = await compressImage(f);
      const fd = new FormData(); fd.append("file", compressed, compressed.name || "photo.jpg");
      try {
        const { data } = await api.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newUrls.push(data.data_url);
      } catch (ex) { setErr(formatApiError(ex)); break; }
    }
    setForm((prev) => ({ ...prev, photos: [...prev.photos, ...newUrls] }));
    setUploading(false); e.target.value = "";
  };

  const removePhoto = (i) => setForm((p) => ({ ...p, photos: p.photos.filter((_, idx) => idx !== i) }));

  const handleTitleInvalid = (e) => {
    e.preventDefault();
    setTitleErr(TITLE_REQUIRED_MESSAGE);
    setErr("");
  };

  const runCheck = async () => {
    if (!form.title || !form.description) { setErr("Add a title and description first."); return; }
    setErr(""); setChecking(true);
    try {
      const { data } = await api.post("/moderation/check", { title: form.title, description: form.description, category: form.category });
      setCheck(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setChecking(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setTitleErr(TITLE_REQUIRED_MESSAGE); setErr(""); return; }
    setTitleErr("");
    if (form.photos.length === 0) { setErr("Add at least one photo of your toy."); return; }
    if (kids.length === 0) { setErr("Add a child profile first — head to Manage kids."); return; }
    // Iteration 56 — the Family Safety selfie/DOB modal has been removed
    // from the listing flow. Parents rely on the queue moderation step
    // (Review & Approve / Release-Reject) in the Approvals Inbox.
    setErr(""); setSubmitting(true);
    try {
      const body = {
        ...form,
        price: parseFloat(form.price),
      };
      // Iteration 274/301 — send clothing attributes when Clothing is the
      // primary OR a secondary pick (stale picks must never leak onto toys).
      if (!clothingPicked) {
        delete body.clothing_subcategory;
        delete body.clothing_gender;
        delete body.clothing_size;
        delete body.clothing_brand;
      } else {
        for (const k of ["clothing_subcategory", "clothing_gender", "clothing_size", "clothing_brand"]) {
          if (!body[k]) delete body[k];
        }
      }
      // Only send the weight override when the parent actually typed one
      // (empty string → let the backend fall back to the 500g default).
      if (form.weight_grams && Number(form.weight_grams) > 0) {
        body.weight_grams = Number(form.weight_grams);
      } else {
        delete body.weight_grams;
      }
      // Iteration 292 — optional item-weight override (kg) for the
      // landfill-impact counters. Blank → the backend AI-estimates it.
      if (form.weight_kg && Number(form.weight_kg) > 0) {
        body.weight_kg = Number(form.weight_kg);
      } else {
        delete body.weight_kg;
      }
      // Iteration 286 — cause tag (only when the toggle is on).
      delete body.fundraise;
      if (form.fundraise && form.cause_kid_id) {
        body.cause_kid_id = form.cause_kid_id;
        body.cause_split_pct = Number(form.cause_split_pct) || 100;
      } else {
        delete body.cause_kid_id;
        delete body.cause_split_pct;
      }
      // Iteration 301b — Best Offer secret floor (mirrors Kid Zone rules):
      // floor required ≥50p, never above the list price; sent as
      // floor_price_pennies (web-tier only, never upstream).
      delete body.floor_pence;
      if (form.pay_what_you_can) {
        if (!(form.floor_pence >= 50)) {
          setErr("Pick a secret floor price (50p or more) — offers at or above it are accepted instantly.");
          setSubmitting(false);
          return;
        }
        if (floorTooHigh) {
          setErr("Secret floor price cannot be higher than list price");
          setSubmitting(false);
          return;
        }
        body.floor_price_pennies = form.floor_pence;
      }
      await api.post("/listings", body);
      navigate("/dashboard/parent");
    } catch (ex) {
      const message = formatApiError(ex);
      if (message.includes("String should have at most 60 characters")) {
        setTitleErr(TITLE_REQUIRED_MESSAGE);
        setErr("");
      } else {
        setErr(message);
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
        <div className="pill bg-brandTeal/15 text-brandTeal" data-testid="sell-eyebrow"><Package className="w-3 h-3 mr-1" /> New listing</div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 mt-3" data-testid="sell-heading">List a toy for sale</h1>
        <p className="text-slate-600 mt-2">Your listing goes through automated safety moderation before going live.</p>

        {kids.length === 0 && (
          <div className="card-base p-6 mt-6 border-amber-200 bg-amber-50/60" data-testid="sell-no-kid-warning">
            <div className="font-bold text-amber-900">Add a child profile first</div>
            <p className="text-sm text-amber-800 mt-1">Listings are attached to one of your children's profiles. <Link to="/kids" className="font-semibold underline">Add a child →</Link></p>
          </div>
        )}

        <form onSubmit={submit} noValidate className="card-base p-6 md:p-8 mt-8 grid lg:grid-cols-2 gap-8" data-testid="sell-form">
          <div>
            {kids.length > 1 && (
              <>
                <label className="block text-sm font-bold text-slate-700 mb-1">Selling on behalf of</label>
                <select className="input-base mb-4" value={form.kid_id}
                  onChange={(e) => setForm({ ...form, kid_id: e.target.value })}
                  data-testid="sell-kid-select">
                  {kids.map((k) => <option key={k.id} value={k.id}>{k.avatar_emoji || "🧒"} {k.name}</option>)}
                </select>
              </>
            )}

            <label htmlFor="sell-title" className={`block text-sm font-bold mb-1 ${titleErr ? "text-rose-700" : "text-slate-700"}`}>
              Listing Title (Required)
            </label>
            <input
              id="sell-title"
              className={`input-base ${titleErr ? "border-rose-500 bg-rose-50/40 ring-2 ring-rose-200 focus:border-rose-500 focus:ring-rose-200" : ""}`}
              required
              minLength={2}
              maxLength={TITLE_LIMIT}
              value={form.title}
              onChange={(e) => {
                setForm({ ...form, title: e.target.value.slice(0, TITLE_LIMIT) });
                if (titleErr) setTitleErr("");
              }}
              onInvalid={handleTitleInvalid}
              aria-invalid={!!titleErr}
              aria-describedby={`${titleErr ? "sell-title-error " : ""}sell-title-counter`}
              placeholder="e.g. Used - Good Condition LEGO Castle set"
              data-testid="sell-title"
            />
            <div className="mt-1 flex items-start justify-between gap-3">
              {titleErr ? (
                <p id="sell-title-error" className="text-xs font-semibold text-rose-700" data-testid="sell-title-error">
                  {titleErr}
                </p>
              ) : <span aria-hidden="true" />}
              <span id="sell-title-counter" className={`ml-auto text-xs font-semibold ${form.title.length >= TITLE_LIMIT ? "text-rose-600" : "text-slate-500"}`} data-testid="sell-title-counter">
                {form.title.length}/{TITLE_LIMIT}
              </span>
            </div>

            <label className="block text-sm font-bold text-slate-700 mb-1 mt-4">Description</label>
            <textarea className="input-base min-h-[120px] resize-y" required minLength={10} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Honest condition, what's included, any missing pieces…" data-testid="sell-description" />

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="col-span-2">
                <label className="block text-sm font-bold text-slate-700 mb-1">Pricing mode</label>
                <div className="inline-flex rounded-full border border-slate-200 p-1" data-testid="sell-price-mode">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, pay_what_you_can: false })}
                    className={`px-3 py-1 text-xs font-bold rounded-full transition ${!form.pay_what_you_can ? "bg-brandPink text-white" : "text-slate-700"}`}
                    data-testid="sell-price-mode-fixed"
                    aria-pressed={!form.pay_what_you_can}
                  >Fixed Price</button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, pay_what_you_can: true })}
                    className={`px-3 py-1 text-xs font-bold rounded-full transition ${form.pay_what_you_can ? "bg-brandPink text-white" : "text-slate-700"}`}
                    data-testid="sell-price-mode-pwyc"
                    aria-pressed={!!form.pay_what_you_can}
                  >Best Offer</button>
                </div>
                {form.pay_what_you_can && (
                  <div className="mt-2 text-[11px] text-slate-500">Best Offer — buyers pick their own amount at checkout. The price you set below acts as the suggested minimum.</div>
                )}
              </div>
              <div className={form.pay_what_you_can ? "col-span-2" : ""}>
                {form.pay_what_you_can ? (
                  /* Iteration 301b — Best Offer dual controls (shared
                     component, exact parity with Kid Zone): list price
                     chips + custom input, secret floor chips + custom
                     input, floor<=list guardrail. */
                  <BestOfferPricing
                    bestOffer
                    pricePence={pricePence}
                    floorPence={form.floor_pence}
                    onPriceChange={(p) => setForm((f) => ({ ...f, price: p ? (p / 100).toFixed(2) : "" }))}
                    onFloorChange={(p) => setForm((f) => ({ ...f, floor_pence: p }))}
                    testIdPrefix="sell-form"
                  />
                ) : (
                  <>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Price (£)</label>
                    <input type="number" min="0.5" step="0.5" required className="input-base" value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      placeholder="9.99" data-testid="sell-price" />
                  </>
                )}
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">
                  Item weight (kg) <span className="font-normal text-slate-400">· optional</span>
                </label>
                <input type="number" min="0.05" max="50" step="0.1" className="input-base" value={form.weight_kg}
                  onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                  placeholder="AI estimated" data-testid="sell-weight-kg" />
                <p className="mt-1 text-[11px] text-slate-500">
                  Powers our landfill-waste impact counters. Leave blank and we'll estimate it with AI from the title, category and photo.
                </p>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Condition</label>
                <ConditionPicker
                  value={form.condition}
                  onChange={(condition) => setForm({ ...form, condition })}
                  testIdPrefix="sell-condition"
                  className="mt-2"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-bold text-slate-700 mb-1">Category</label>
                <select className="input-base" value={form.category}
                  onChange={(e) => setForm({
                    ...form,
                    category: e.target.value,
                    // Auto-drop the just-picked primary from the secondary
                    // list so a category can never appear twice.
                    secondary_categories: (form.secondary_categories || []).filter((v) => v !== e.target.value),
                  })} data-testid="sell-category">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label} {c.ageHint && `· ${c.ageHint}`}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-bold text-slate-700 mb-1">
                  Secondary Categories <span className="font-normal text-slate-400">· Pick up to 2 (optional)</span>
                </label>
                <div className="flex flex-wrap gap-2" data-testid="sell-secondary-categories">
                  {CATEGORIES.filter((c) => c.value !== form.category).map((c) => {
                    const picked = (form.secondary_categories || []).includes(c.value);
                    const disabled = !picked && (form.secondary_categories || []).length >= 2;
                    return (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => {
                          const current = form.secondary_categories || [];
                          const next = picked
                            ? current.filter((v) => v !== c.value)
                            : [...current, c.value];
                          setForm({ ...form, secondary_categories: next });
                        }}
                        disabled={disabled}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                          picked
                            ? "bg-brandPink text-white border-brandPink"
                            : disabled
                              ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                              : "bg-white text-slate-700 border-slate-200 hover:border-brandPink hover:text-brandPink"
                        }`}
                        data-testid={`sell-secondary-chip-${c.value}`}
                        aria-pressed={picked}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] text-slate-500 mt-1.5" data-testid="sell-secondary-count">
                  {(form.secondary_categories || []).length}/2 selected · Buyers browsing any picked category will see this listing.
                </div>
              </div>
              {/* Iteration 301 — Clothing attributes. Shared tap-chip
                  panel (exact parity with the Kid Zone form); expands when
                  Clothing is the primary OR a secondary category pick. */}
              {clothingPicked && (
                <div className="col-span-2" data-testid="sell-clothing-attrs">
                  <ClothingPickers
                    testIdPrefix="sell-clothing"
                    values={form}
                    onChange={(updates) => setForm((f) => ({ ...f, ...updates }))}
                  />
                </div>
              )}
            </div>

            <div className="mt-4 card-base p-4 border border-slate-200" data-testid="sell-shipping-card">
              <div className="flex items-center justify-between">
                <div className="font-bold text-slate-800 inline-flex items-center gap-2"><Truck className="w-4 h-4 text-blue-500" /> Allow shipping?</div>
                <label className="inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={form.shipping_available}
                    onChange={(e) => setForm({ ...form, shipping_available: e.target.checked })}
                    data-testid="sell-shipping-toggle" />
                  <div className="relative w-11 h-6 bg-slate-200 rounded-full peer-checked:bg-blue-500 transition-all">
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.shipping_available ? "translate-x-5" : ""}`} />
                  </div>
                </label>
              </div>
              {form.shipping_available && (
                <>
                  <select className="input-base mt-3 !py-2" value={form.package_size}
                    onChange={(e) => setForm({ ...form, package_size: e.target.value })}
                    data-testid="sell-package-size">
                    {PACKAGE_SIZES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  {/* Iteration 75 — surface fulfillment expectation right on the form. */}
                  {form.package_size === "bulky" ? (
                    <p className="mt-2 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="sell-bulky-note">
                      Bulky items need a parent to pick the delivery option
                      (Parent Handoff, Collection Only, Delivery Only,
                      or Royal Mail Post) during approval.
                    </p>
                  ) : (
                    <p className="mt-2 text-[11px] text-slate-500" data-testid="sell-royal-mail-note">
                      Ships via <strong>Royal Mail Post</strong>. The seller posts directly at the Post Office.
                    </p>
                  )}
                  {/* Iteration 174 — parcel weight override */}
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mt-3 mb-1">Parcel weight (grams)</label>
                  <input
                    type="number"
                    min="1"
                    max="30000"
                    placeholder="500"
                    value={form.weight_grams}
                    onChange={(e) => setForm({ ...form, weight_grams: e.target.value.replace(/[^0-9]/g, "") })}
                    className="input-base !py-2"
                    data-testid="sell-weight-grams"
                  />
                  <p className="mt-1 text-[11px] text-slate-500">Defaults to 500g when blank. Use the parcel's real weight for heavier items (e.g. large LEGO sets) so the correct Royal Mail service is picked.</p>
                </>
              )}
            </div>

            <label className="block text-sm font-bold text-slate-700 mb-1 mt-5 flex items-center gap-2"><Camera className="w-4 h-4" /> Photos ({form.photos.length}/{MAX_PHOTOS})</label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3" data-testid="sell-photo-grid">
              {form.photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 group">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-white/90 hover:bg-rose-500 hover:text-white flex items-center justify-center shadow-soft transition-all"
                    data-testid={`sell-photo-remove-${i}`}><X className="w-4 h-4" /></button>
                  {i === 0 && <span className="absolute bottom-1 left-1 pill bg-amber-400 text-amber-950 !text-[10px]">Cover</span>}
                </div>
              ))}
              {form.photos.length < MAX_PHOTOS && (
                <label className={`aspect-square rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 cursor-pointer flex flex-col items-center justify-center text-slate-500 transition-all ${uploading ? "pointer-events-none opacity-60" : ""}`} data-testid="sell-photo-upload-label">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<><Upload className="w-5 h-5" /><span className="text-xs font-bold mt-1">Add</span></>)}
                  <input type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="sell-photo-input" />
                </label>
              )}
            </div>
            <p className="text-xs text-slate-500">JPG, PNG or HEIC up to 8MB each. We auto-resize. The first photo is your cover.</p>
          </div>

          <div className="space-y-4">
            <div className="card-base p-5 border border-slate-200" data-testid="ai-helper-card">
              <div className="flex items-center justify-between">
                <div className="font-display text-lg font-medium text-slate-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-500" /> Safety helper</div>
                <button type="button" onClick={runCheck} disabled={checking}
                  className="btn-secondary !py-2 !px-4 text-sm" data-testid="ai-check-btn">
                  {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run check"}
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-2">Quick pre-check. Our marketplace also runs its own moderation when you submit.</p>
              {check && (
                <div className={`mt-4 rounded-2xl p-4 border ${check.approved ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`} data-testid="ai-feedback">
                  <div className={`font-bold flex items-center gap-2 ${check.approved ? "text-emerald-800" : "text-rose-800"}`}>
                    {check.approved ? <ShieldCheck className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    {check.approved ? "Looks great" : "Worth tweaking"}
                  </div>
                  {check.reason && <div className="text-xs text-slate-700 mt-1">{check.reason}</div>}
                  {check.flags?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {check.flags.map((f) => <span key={f} className="pill bg-white text-slate-700 border border-slate-200">{f}</span>)}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="card-base p-5">
              <div className="text-sm font-bold text-slate-700 mb-3">What happens next?</div>
              <ol className="text-sm text-slate-600 space-y-2 list-decimal pl-5">
                <li>The marketplace runs an automated safety check.</li>
                <li>If clean, it goes live immediately. If flagged, you'll see it in your moderation queue.</li>
                <li>Buyers pay via Stripe — funds go to your wallet.</li>
              </ol>
            </div>

            {/* Iteration 286 — Fundraise for a Cause. Tags the listing to
                a verified Organization sub-account with a proceeds split. */}
            <div className="card-base p-5 border border-rose-200 bg-rose-50/40" data-testid="sell-cause-block">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.fundraise}
                  onChange={(e) => setForm({ ...form, fundraise: e.target.checked, cause_kid_id: e.target.checked ? form.cause_kid_id : "" })}
                  className="w-4 h-4 accent-rose-600"
                  data-testid="sell-cause-toggle"
                />
                <span className="text-sm font-bold text-slate-800 inline-flex items-center gap-1.5">
                  <HeartHandshake className="w-4 h-4 text-rose-600" /> Fundraise for a Cause
                </span>
              </label>
              {form.fundraise && (
                <div className="mt-3">
                  {causes.filter((c) => c.verified).length === 0 ? (
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="sell-cause-none">
                      No verified causes in your household yet — add an Organization / Cause sub-account under Manage kids and wait for verification.
                    </p>
                  ) : (
                    <>
                      <label className="block text-sm font-bold text-slate-700 mb-1">Cause</label>
                      <select
                        className="input-base mb-3"
                        value={form.cause_kid_id}
                        onChange={(e) => setForm({ ...form, cause_kid_id: e.target.value })}
                        data-testid="sell-cause-select"
                      >
                        <option value="">Select a cause…</option>
                        {causes.filter((c) => c.verified).map((c) => (
                          <option key={c.kid_id} value={c.kid_id}>{c.avatar_emoji || "🏫"} {c.name}</option>
                        ))}
                      </select>
                      <div className="text-sm font-bold text-slate-700 mb-2">Proceeds split</div>
                      <div className="flex flex-wrap gap-2">
                        {[100, 75, 50, 25].map((p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => setForm({ ...form, cause_split_pct: p })}
                            className={`px-3 py-1.5 rounded-full text-[12px] font-bold border transition ${form.cause_split_pct === p ? "bg-rose-600 text-white border-rose-600" : "bg-white text-slate-700 border-slate-200 hover:border-rose-300"}`}
                            data-testid={`sell-cause-split-${p}`}
                          >
                            {p === 100 ? "100% Donation" : `${p}% to cause`}
                          </button>
                        ))}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-2">
                        {form.cause_split_pct}% of net proceeds go to the cause; the rest lands in your seller wallet.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

            {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="sell-error">{err}</div>}

            {/* Parent moderation choice — decides whether the listing lands
                in the Live grid or the Pending queue. */}
            <div className="card-base p-5 border border-slate-200" data-testid="sell-moderation-picker">
              <div className="text-sm font-bold text-slate-700 mb-2">Publishing preference</div>
              <div className="space-y-2">
                <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${!form.publish_directly ? "border-brandTeal bg-brandTeal/5" : "border-slate-200 hover:border-slate-300"}`} data-testid="sell-mode-approval">
                  <input
                    type="radio"
                    name="publish_mode"
                    checked={!form.publish_directly}
                    onChange={() => setForm({ ...form, publish_directly: false })}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-bold text-slate-800 text-sm">Submit for approval</div>
                    <div className="text-xs text-slate-500 mt-0.5">The listing lands in your Pending Approvals queue so you can review before it goes live. Recommended for kid-authored listings.</div>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${form.publish_directly ? "border-brandPink bg-brandPink/5" : "border-slate-200 hover:border-slate-300"}`} data-testid="sell-mode-direct">
                  <input
                    type="radio"
                    name="publish_mode"
                    checked={form.publish_directly}
                    onChange={() => setForm({ ...form, publish_directly: true })}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-bold text-slate-800 text-sm">Publish directly</div>
                    <div className="text-xs text-slate-500 mt-0.5">You&rsquo;re the moderator — the listing goes straight to Live and updates the household counter immediately.</div>
                  </div>
                </label>
              </div>
            </div>

            <button type="submit" disabled={submitting || uploading || kids.length === 0 || floorTooHigh}
              className="btn-primary w-full" data-testid="sell-submit-btn">
              {submitting ? "Submitting…" : (
                <>{form.publish_directly ? "Publish now" : "Submit for approval"} <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        </form>
      </section>
      <Footer />
    </div>
  );
}
